import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  goodsReturnsToSupplier,
  type GRTSListItem,
} from '@/api/goodsReturns';
import { contractors as contractorsApi } from '@/api/catalogs';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import type { ColDef } from 'ag-grid-community';
import ImportedBadge from '@/shared/components/ImportedBadge';
import InfiniteDocGrid, { TitleTotal } from '@/shared/components/InfiniteDocGrid';
import { useInfiniteGridDatasource } from '@/hooks/useInfiniteGridDatasource';
import { useSignControlInfinite } from '@/shared/hooks/useSignControl';
import DocListFilters, { ContractorFilter, DocListFiltersRow, useDocListFilters } from '@/shared/components/DocListFilters';
import { agOrgCol, useOrgNames } from '@/shared/hooks/useOrgNames';

const fmt = (v: string) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function GoodsReturnsToSupplierPage() {
  const orgNames = useOrgNames();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  // Стандартні фільтри списків (еталон GoodsReturnsFromCustomerPage ч.225).
  const f = useDocListFilters();
  // 2026-07-31: пошуковий фільтр за постачальником.
  const [contractorFilter, setContractorFilter] = useState('');
  // 2026-08-09 (Норма #29): серверна пагінація + сортування.
  const { datasource, total } = useInfiniteGridDatasource<GRTSListItem>(
    (limit, offset, sort) => goodsReturnsToSupplier.list({
      limit, offset,
      sort_by: sort?.sort_by, sort_dir: sort?.sort_dir,
      organization_id: f.orgId || undefined,
      contractor_id: contractorFilter || undefined,
      posted: f.status === 'posted' ? true : f.status === 'draft' ? false : undefined,
      date_from: f.dateFrom, date_to: f.dateTo,
    }),
    [contractorFilter, ...f.queryKey],
    { attachmentsType: 'doc.goods_returns_to_supplier' },
  );
  // Контроль підписання (0429): колонка «Підпис» + фон непідписаних.
  const sc = useSignControlInfinite<GRTSListItem>('doc.goods_returns_to_supplier', f.orgId);
  const {
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
  } = usePermissionsByOrg('purchases.return.manage');
  const cQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.listAll(),
  });
  const cName = useMemo(() => {
    const m = new Map<string, string>();
    cQ.data?.items.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [cQ.data]);

  const cols = useMemo<ColDef<GRTSListItem>[]>(() => [
    {
      headerName: '', width: 44, sortable: false,
      cellRenderer: (p: { data?: GRTSListItem }) =>
        <ImportedBadge source={p.data?.external_source} />,
    },
    { field: 'document_number', headerName: '№', width: 150, sortable: true },
    {
      field: 'document_date', headerName: t('cd.date'), width: 170, sortable: true,
      valueFormatter: (p) => p.value ? new Date(p.value).toLocaleString(i18n.language) : '',
    },
    agOrgCol(orgNames),
    {
      field: 'contractor_id', headerName: 'Постачальник', flex: 1, minWidth: 220, sortable: false,
      valueFormatter: (p) => cName.get(p.value) ?? '',
    },
    {
      field: 'total_amount', headerName: t('cd.amount'), width: 140, sortable: true,
      cellStyle: { textAlign: 'right' },
      valueFormatter: (p) => p.value != null ? fmt(p.value) : '',
    },
    {
      colId: '_att', headerName: 'Вкладення', width: 110, sortable: false,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { _att?: boolean } }) =>
        p.data?._att ? '✓' : '',
    },
    {
      field: 'posted', headerName: t('cd.posted'), width: 130, sortable: true,
      cellStyle: { textAlign: 'center' },
      cellRenderer: (p: { data?: { posted?: boolean } }) =>
        p.data?.posted ? <span style={{ color: 'var(--badge-green-fg)', fontWeight: 600 }}>✓</span> : '',
    },
  ], [t, i18n.language, cName]);

  return (
    <div className="card">
      <div className="toolbar" style={{ gap: '0.4rem', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Повернення постачальнику<TitleTotal total={total} /></h2>
        <button
          className="primary"
          style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/goods-returns-to-supplier/new')}
          disabled={!hasPermissionInAnyOrg}
          title={!hasPermissionInAnyOrg
            ? `У вас немає прав створювати повернення у жодній з ${totalAccessibleOrgs} ваших організацій`
            : undefined
          }
        >
          + Нове повернення
        </button>
      </div>
      <DocListFiltersRow>
        <DocListFilters f={f} statusOptions={[
          { value: '', label: 'Усі статуси' },
          { value: 'posted', label: 'Проведені' },
          { value: 'draft', label: 'Чернетки' },
        ]} />
        <ContractorFilter value={contractorFilter} onChange={setContractorFilter}
          label="постачальник" />
      </DocListFiltersRow>
      <InfiniteDocGrid<GRTSListItem>
        copyRow={(r) => navigate(`/goods-returns-to-supplier/new?copy=${r.id}`)}
        deleteRow={{ remove: (id) => goodsReturnsToSupplier.remove(id) }}
        datasource={sc.wrap(datasource)}
        columnDefs={[...cols, ...sc.column]}
        storageKey="grts"
        onRowDoubleClicked={(r) => navigate(`/goods-returns-to-supplier/${r.id}`)}
        getRowStyle={(p) => p.data?.deletion_mark ? { opacity: 0.4 } : sc.getRowStyle(p)}
      />
    </div>
  );
}
